import React, { useMemo, useState } from 'react';

const INNER_TABS = [
  ['now', 'Сейчас'],
  ['visitors', 'Посетители'],
  ['users', 'Пользователи'],
  ['sessions', 'Сессии'],
  ['blocked', 'Заблокированные'],
];

const formatAgo = (value) => {
  const timestamp = typeof value === 'number' ? value : new Date(value || 0).getTime();
  if (!timestamp) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds} сек`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч`;
  return `${Math.floor(seconds / 86400)} д`;
};

const formatDate = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—';
const formatDuration = (seconds) => {
  const value = Math.max(0, Number(seconds || 0));
  const d = Math.floor(value / 86400);
  const h = Math.floor((value % 86400) / 3600);
  const m = Math.floor((value % 3600) / 60);
  if (d) return `${d}д ${h}ч`;
  if (h) return `${h}ч ${m}м`;
  if (m) return `${m}м`;
  return `${Math.round(value)}с`;
};

const shortVisitor = (value) => {
  const text = String(value || 'guest');
  return text.length > 10 ? text.slice(-8).toUpperCase() : text.toUpperCase();
};

const roleLabel = (value) => ({
  guest: 'ГОСТЬ',
  user: 'ПОЛЬЗОВАТЕЛЬ',
  admin: 'АДМИНИСТРАТОР',
  superAdmin: 'СУПЕРАДМИН',
}[String(value || '')] || String(value || 'ПОЛЬЗОВАТЕЛЬ').toUpperCase());

const filterGroupBySource = (group, source) => {
  if (source === 'all') return group;
  const connections = (group.connections || []).filter((item) => item.app === source);
  if (!connections.length) return null;
  return {
    ...group,
    connections,
    connectionCount: connections.length,
    clientCount: new Set(connections.map((item) => item.clientId || item.socketId)).size,
    apps: [source],
    routes: Array.from(new Set(connections.map((item) => item.route).filter(Boolean))),
    lastActivityAt: Math.max(...connections.map((item) => Number(item.lastActivityAt || 0)), 0),
  };
};

const SourceBadges = ({ sources = [] }) => (
  <span className="systemMonitor__inlineBadges">
    {Array.from(new Set(sources)).map((app) => (
      <span key={app} className={`systemMonitor__appBadge is-${app}`}>{app === 'admin' ? 'АДМИНКА' : 'САЙТ'}</span>
    ))}
  </span>
);

const ConnectionRow = ({ connection, onDisconnectConnection, busy }) => (
  <div className="systemMonitor__connection">
    <div className="systemMonitor__connectionMain">
      <div className="systemMonitor__connectionTop">
        <span className={`systemMonitor__appBadge is-${connection.app || 'front'}`}>{connection.app === 'admin' ? 'АДМИНКА' : 'САЙТ'}</span>
        <strong>{connection.deviceLabel || 'Устройство'} · {connection.browserLabel || 'Браузер'}</strong>
        <span className={connection.visibility === 'hidden' ? 'is-muted' : 'is-online'}>{connection.visibility === 'hidden' ? 'фон' : 'активная вкладка'}</span>
      </div>
      <div className="systemMonitor__connectionMeta">
        <span>{connection.route || 'маршрут не передан'}</span>
        <span>{connection.transport || '—'}</span>
        {connection.ip ? <span>IP {connection.ip}</span> : null}
        {connection.visitorId ? <span>ID посетителя {shortVisitor(connection.visitorId)}</span> : null}
        <span>активность {formatAgo(connection.lastActivityAt)} назад</span>
      </div>
    </div>
    <button type="button" className="systemMonitor__miniBtn" disabled={busy} onClick={() => onDisconnectConnection(connection)}>Отключить</button>
  </div>
);

const LiveUserCard = ({ user, currentUserId, onDisconnectUser, onDisconnectConnection, onBlock, onUnblock, onHistory, busyKey }) => {
  const [expanded, setExpanded] = useState(false);
  const isSelf = String(user.userId || '') === String(currentUserId || '');
  const protectedSuperAdmin = user.role === 'superAdmin';
  const busy = busyKey === user.userId || busyKey === `disconnect:${user.userId}`;

  return (
    <article className={`systemMonitor__userCard${user.isActive === false ? ' is-blocked' : ''}`}>
      <div className="systemMonitor__userHead">
        <div className="systemMonitor__userIdentity">
          <div className="systemMonitor__avatar">{String(user.name || 'U').charAt(0).toUpperCase()}</div>
          <div>
            <div className="systemMonitor__userNameRow">
              <strong>{user.name || 'Пользователь'}</strong>
              <span className="systemMonitor__roleBadge">{roleLabel(user.role)}</span>
              {user.isActive === false ? <span className="systemMonitor__blockedBadge">ЗАБЛОКИРОВАН</span> : <span className="systemMonitor__onlineBadge">В СЕТИ</span>}
              <SourceBadges sources={user.apps || []} />
            </div>
            <div className="systemMonitor__userSub">{user.email || user.userId || '—'} · {user.routes?.[0] || 'страница —'}</div>
          </div>
        </div>
        <div className="systemMonitor__userStats">
          <span><b>{user.connectionCount || 0}</b> сокетов</span>
          <span><b>{user.clientCount || 0}</b> вкладки</span>
          <span>активность {formatAgo(user.lastActivityAt)} назад</span>
        </div>
      </div>

      <div className="systemMonitor__userActions">
        <button type="button" className="systemMonitor__miniBtn" onClick={() => onHistory(user)}>Полная история</button>
        <button type="button" className="systemMonitor__miniBtn" onClick={() => setExpanded((value) => !value)}>{expanded ? 'Скрыть соединения' : 'Соединения'}</button>
        {!isSelf ? <button type="button" className="systemMonitor__miniBtn" disabled={busy} onClick={() => onDisconnectUser(user)}>Отключить все</button> : null}
        {!isSelf && !protectedSuperAdmin && user.isActive !== false ? <button type="button" className="systemMonitor__miniBtn is-danger" disabled={busy} onClick={() => onBlock(user)}>Заблокировать</button> : null}
        {!isSelf && user.isActive === false ? <button type="button" className="systemMonitor__miniBtn is-success" disabled={busy} onClick={() => onUnblock(user)}>Разблокировать</button> : null}
      </div>

      {expanded ? <div className="systemMonitor__connections">{(user.connections || []).map((connection) => (
        <ConnectionRow key={connection.socketId} connection={connection} onDisconnectConnection={onDisconnectConnection} busy={busyKey === connection.socketId} />
      ))}</div> : null}
    </article>
  );
};

const LiveGuestCard = ({ guest, onDisconnectConnection, onVisitorHistory, busyKey }) => {
  const visitorId = guest.visitorId || guest.connections?.[0]?.visitorId || guest.connections?.[0]?.clientId || guest.key;
  return (
    <article className="systemMonitor__userCard is-guest">
      <div className="systemMonitor__userHead">
        <div className="systemMonitor__userIdentity">
          <div className="systemMonitor__avatar">G</div>
          <div>
            <div className="systemMonitor__userNameRow"><strong>Гость #{shortVisitor(visitorId)}</strong><span className="systemMonitor__roleBadge">ГОСТЬ</span><span className="systemMonitor__onlineBadge">В СЕТИ</span><SourceBadges sources={guest.apps || []} /></div>
            <div className="systemMonitor__userSub">{guest.routes?.[0] || 'страница не определена'} · ID посетителя {visitorId}</div>
          </div>
        </div>
        <div className="systemMonitor__userStats"><span><b>{guest.connectionCount || 0}</b> сокетов</span><span><b>{guest.clientCount || 0}</b> вкладки</span><span>активность {formatAgo(guest.lastActivityAt)} назад</span></div>
      </div>
      <div className="systemMonitor__userActions"><button type="button" className="systemMonitor__miniBtn" onClick={() => onVisitorHistory({ visitorId })}>История гостя</button></div>
      <div className="systemMonitor__connections is-open">{(guest.connections || []).map((connection) => (
        <ConnectionRow key={connection.socketId} connection={connection} onDisconnectConnection={onDisconnectConnection} busy={busyKey === connection.socketId} />
      ))}</div>
    </article>
  );
};

const VisitorCard = ({ visitor, onVisitorHistory }) => {
  const linked = visitor.linkedUser;
  return (
    <article className={`systemMonitor__visitorCard${visitor.online ? ' is-online' : ''}${visitor.guest ? ' is-guest' : ''}`}>
      <div className="systemMonitor__visitorTop">
        <div className="systemMonitor__userIdentity">
          <div className="systemMonitor__avatar">{linked ? String(linked.name || 'U').charAt(0).toUpperCase() : 'G'}</div>
          <div>
            <div className="systemMonitor__userNameRow">
              <strong>{linked?.name || `Гость #${shortVisitor(visitor.visitorId)}`}</strong>
              <span className="systemMonitor__roleBadge">{linked?.role || 'guest'}</span>
              {visitor.online ? <span className="systemMonitor__onlineBadge">В СЕТИ</span> : <span className="systemMonitor__offlineBadge">НЕ В СЕТИ</span>}
              <SourceBadges sources={visitor.sources || [visitor.lastSource].filter(Boolean)} />
            </div>
            <div className="systemMonitor__userSub">ID посетителя {visitor.visitorId}{linked?.email ? ` · ${linked.email}` : ''}</div>
          </div>
        </div>
        <button type="button" className="systemMonitor__miniBtn" onClick={() => onVisitorHistory(visitor)}>Открыть историю</button>
      </div>
      <div className="systemMonitor__visitorMetrics">
        <div><span>Последний визит</span><b>{formatAgo(visitor.lastSeenAt)} назад</b></div>
        <div><span>Сессий</span><b>{visitor.sessions || 0}</b></div>
        <div><span>Страниц</span><b>{visitor.pageViews || 0}</b></div>
        <div><span>Активное время</span><b>{formatDuration(visitor.activeSeconds)}</b></div>
      </div>
      <div className="systemMonitor__visitorFoot">
        <span>{visitor.lastRoute || '/'}</span>
        <span>{visitor.deviceLabel || 'Устройство'} · {visitor.browserLabel || 'Браузер'}</span>
        {visitor.lastIp ? <span>IP {visitor.lastIp}</span> : null}
        <span>первый в периоде {formatDate(visitor.firstSeenAt)}</span>
      </div>
    </article>
  );
};

const HistoricalUserCard = ({ user, currentUserId, onBlock, onUnblock, onHistory, busyKey }) => {
  const isSelf = String(user.userId || '') === String(currentUserId || '');
  const protectedSuperAdmin = user.role === 'superAdmin';
  return (
    <article className={`systemMonitor__visitorCard${user.online ? ' is-online' : ''}${user.isActive === false ? ' is-blocked' : ''}`}>
      <div className="systemMonitor__visitorTop">
        <div className="systemMonitor__userIdentity">
          <div className="systemMonitor__avatar">{String(user.name || 'U').charAt(0).toUpperCase()}</div>
          <div><div className="systemMonitor__userNameRow"><strong>{user.name || 'Пользователь'}</strong><span className="systemMonitor__roleBadge">{roleLabel(user.role)}</span>{user.online ? <span className="systemMonitor__onlineBadge">В СЕТИ</span> : <span className="systemMonitor__offlineBadge">НЕ В СЕТИ</span>}{user.isActive === false ? <span className="systemMonitor__blockedBadge">ЗАБЛОКИРОВАН</span> : null}<SourceBadges sources={user.sources || []} /></div><div className="systemMonitor__userSub">{user.email || user.userId}</div></div>
        </div>
        <div className="systemMonitor__userActions is-inline">
          <button type="button" className="systemMonitor__miniBtn" onClick={() => onHistory(user)}>История</button>
          {!isSelf && !protectedSuperAdmin && user.isActive !== false ? <button type="button" className="systemMonitor__miniBtn is-danger" disabled={busyKey === user.userId} onClick={() => onBlock(user)}>Заблокировать</button> : null}
          {!isSelf && user.isActive === false ? <button type="button" className="systemMonitor__miniBtn is-success" disabled={busyKey === user.userId} onClick={() => onUnblock(user)}>Разблокировать</button> : null}
        </div>
      </div>
      <div className="systemMonitor__visitorMetrics"><div><span>Последний визит</span><b>{formatAgo(user.lastSeenAt)} назад</b></div><div><span>Сессий</span><b>{user.sessions || 0}</b></div><div><span>Страниц</span><b>{user.pageViews || 0}</b></div><div><span>Активное время</span><b>{formatDuration(user.activeSeconds)}</b></div></div>
      <div className="systemMonitor__visitorFoot"><span>{user.lastRoute || '/'}</span><span>{user.deviceLabel || 'Устройство'} · {user.browserLabel || 'Браузер'}</span>{user.lastIp ? <span>IP {user.lastIp}</span> : null}<span>{(user.visitorIds || []).length} ID посетителя</span></div>
    </article>
  );
};

const SessionRow = ({ session, onVisitorHistory, onHistory }) => (
  <div className="systemMonitor__sessionRow">
    <div><strong>{session.user?.name || `Гость #${shortVisitor(session.visitorId)}`}</strong><small>{session.user?.email || session.visitorId}</small></div>
    <div><SourceBadges sources={[session.source]} /><small>{session.deviceLabel || 'Устройство'} · {session.browserLabel || 'Браузер'}</small></div>
    <div><strong>{session.lastRoute || session.firstRoute || '/'}</strong><small>{session.ip ? `IP ${session.ip}` : 'IP —'}</small></div>
    <div><strong>{formatDuration(session.activeSeconds)}</strong><small>{session.pageViews || 0} стр.</small></div>
    <div><strong>{session.online ? 'В СЕТИ' : formatAgo(session.lastSeenAt)}</strong><small>{formatDate(session.startedAt)}</small></div>
    <button type="button" className="systemMonitor__miniBtn" onClick={() => session.user ? onHistory({ ...session.user, userId: session.user._id }) : onVisitorHistory({ visitorId: session.visitorId })}>История</button>
  </div>
);

const matches = (query, values) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return values.filter(Boolean).join(' ').toLowerCase().includes(needle);
};

const SystemMonitorUsers = ({
  users = [], guests = [], directory = null, directoryLoading = false, visitorDays = 1, onVisitorDaysChange,
  blockedUsers = [], source = 'all', currentUserId, onDisconnectUser, onDisconnectConnection,
  onBlock, onUnblock, onHistory, onVisitorHistory, busyKey,
}) => {
  const [innerTab, setInnerTab] = useState('now');
  const [query, setQuery] = useState('');

  const sourceUsers = useMemo(() => users.map((item) => filterGroupBySource(item, source)).filter(Boolean), [source, users]);
  const sourceGuests = useMemo(() => guests.map((item) => filterGroupBySource(item, source)).filter(Boolean), [guests, source]);
  const savedVisitors = directory?.visitors || [];
  const savedUsers = directory?.users || [];
  const sessions = directory?.sessions || [];
  const stats = directory?.stats || {};

  const filteredLiveUsers = sourceUsers.filter((user) => matches(query, [user.name, user.email, user.role, user.userId, ...(user.routes || [])]));
  const filteredLiveGuests = sourceGuests.filter((guest) => matches(query, [guest.visitorId, guest.key, ...(guest.routes || []), ...(guest.connections || []).flatMap((c) => [c.ip, c.deviceLabel, c.browserLabel])]));
  const filteredVisitors = savedVisitors.filter((visitor) => matches(query, [visitor.visitorId, visitor.linkedUser?.name, visitor.linkedUser?.email, visitor.lastIp, visitor.lastRoute, visitor.deviceLabel, visitor.browserLabel]));
  const filteredSavedUsers = savedUsers.filter((user) => matches(query, [user.name, user.email, user.role, user.userId, user.lastIp, user.lastRoute]));
  const filteredSessions = sessions.filter((session) => matches(query, [session.visitorId, session.user?.name, session.user?.email, session.ip, session.lastRoute, session.deviceLabel, session.browserLabel]));

  return (
    <>
      <div className="systemMonitor__visitorKpis">
        <div><span>Сейчас в сети</span><strong>{sourceUsers.length + sourceGuests.length}</strong><small>{sourceUsers.length} пользователей · {sourceGuests.length} гостей</small></div>
        <div><span>Уникальных посетителей</span><strong>{stats.uniqueVisitors || 0}</strong><small>за выбранный период</small></div>
        <div><span>Гостей</span><strong>{stats.guestVisitors || 0}</strong><small>без связанного аккаунта</small></div>
        <div><span>Авторизованных</span><strong>{stats.uniqueUsers || 0}</strong><small>уникальные аккаунты</small></div>
        <div><span>Вернувшихся</span><strong>{stats.returningVisitors || 0}</strong><small>2+ сессии</small></div>
        <div><span>Сессий</span><strong>{stats.sessions || 0}</strong><small>{stats.pageViews || 0} просмотров</small></div>
      </div>

      <section className="systemMonitor__section systemMonitor__peopleSection">
        <div className="systemMonitor__sectionHead">
          <div><div className="systemMonitor__sectionTitle">Люди и посетители</div><div className="systemMonitor__sectionHint">Текущие соединения не смешиваются с историей: гости сохраняются по постоянному ID посетителя даже после отключения сокетов.</div></div>
          <div className="systemMonitor__usersTools">
            <input className="systemMonitor__search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Имя, email, IP, ID посетителя, страница" />
            <div className="systemMonitor__periodSwitch is-small">{[1, 7, 30, 90].map((days) => <button key={days} type="button" className={visitorDays === days ? 'is-active' : ''} onClick={() => onVisitorDaysChange(days)}>{days === 1 ? 'Сегодня' : `${days}д`}</button>)}</div>
          </div>
        </div>

        <div className="systemMonitor__peopleTabs">{INNER_TABS.map(([key, label]) => <button key={key} type="button" className={innerTab === key ? 'is-active' : ''} onClick={() => setInnerTab(key)}>{label}<span>{key === 'now' ? sourceUsers.length + sourceGuests.length : key === 'visitors' ? savedVisitors.length : key === 'users' ? savedUsers.length : key === 'sessions' ? sessions.length : blockedUsers.length}</span></button>)}</div>

        {directoryLoading ? <div className="systemMonitor__peopleLoading">Обновляю сохранённую историю…</div> : null}

        {innerTab === 'now' ? <div className="systemMonitor__usersList">
          {filteredLiveUsers.map((user) => <LiveUserCard key={user.key || user.userId} user={user} currentUserId={currentUserId} onDisconnectUser={onDisconnectUser} onDisconnectConnection={onDisconnectConnection} onBlock={onBlock} onUnblock={onUnblock} onHistory={onHistory} busyKey={busyKey} />)}
          {filteredLiveGuests.map((guest) => <LiveGuestCard key={guest.key} guest={guest} onDisconnectConnection={onDisconnectConnection} onVisitorHistory={onVisitorHistory} busyKey={busyKey} />)}
          {!filteredLiveUsers.length && !filteredLiveGuests.length ? <div className="systemMonitor__empty">Сейчас подходящих соединений нет.</div> : null}
        </div> : null}

        {innerTab === 'visitors' ? <div className="systemMonitor__visitorList">{filteredVisitors.map((visitor) => <VisitorCard key={visitor.visitorId} visitor={visitor} onVisitorHistory={onVisitorHistory} />)}{!filteredVisitors.length ? <div className="systemMonitor__empty">Посетителей за этот период пока нет.</div> : null}</div> : null}

        {innerTab === 'users' ? <div className="systemMonitor__visitorList">{filteredSavedUsers.map((user) => <HistoricalUserCard key={user.userId} user={user} currentUserId={currentUserId} onBlock={onBlock} onUnblock={onUnblock} onHistory={onHistory} busyKey={busyKey} />)}{!filteredSavedUsers.length ? <div className="systemMonitor__empty">Авторизованных посетителей за период нет.</div> : null}</div> : null}

        {innerTab === 'sessions' ? <div className="systemMonitor__sessionList">{filteredSessions.map((session) => <SessionRow key={session.sessionId} session={session} onVisitorHistory={onVisitorHistory} onHistory={onHistory} />)}{!filteredSessions.length ? <div className="systemMonitor__empty">Сессий за период нет.</div> : null}</div> : null}

        {innerTab === 'blocked' ? <div className="systemMonitor__visitorList">{blockedUsers.map((blockedUser) => <article className="systemMonitor__visitorCard is-blocked" key={blockedUser._id}><div className="systemMonitor__visitorTop"><div><div className="systemMonitor__userNameRow"><strong>{blockedUser.name}</strong><span className="systemMonitor__blockedBadge">ЗАБЛОКИРОВАН</span><span className="systemMonitor__roleBadge">{blockedUser.role}</span></div><div className="systemMonitor__userSub">{blockedUser.email} · изменено {formatDate(blockedUser.updatedAt)}</div></div><button type="button" className="systemMonitor__miniBtn is-success" disabled={busyKey === String(blockedUser._id)} onClick={() => onUnblock(blockedUser)}>Разблокировать</button></div></article>)}{!blockedUsers.length ? <div className="systemMonitor__empty">Заблокированных аккаунтов нет.</div> : null}</div> : null}
      </section>
    </>
  );
};

export default SystemMonitorUsers;
